import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { PageHeader } from '@/components/PageHeader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sun, Moon, Lock, Plug, Calendar, Video, Building2, CreditCard, Landmark, Check, Info, Camera, Download, IdCard, Languages, Database, Cloud, HardDriveDownload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const INTEGRATIONS = [
  { id: 'google_cal', icon: Calendar, name: 'Google Calendar', desc: 'Sincroniza eventos bidireccional.', steps: 'Fase de integraciones: requiere Client ID/Secret de Google Cloud (OAuth2). Cada usuario conecta su propia cuenta de forma confidencial.' },
  { id: 'outlook', icon: Building2, name: 'Microsoft Outlook', desc: 'Calendario y correo de Microsoft 365.', steps: 'Requiere registro en Azure AD (Microsoft Graph). Conexión por-usuario mediante OAuth2.' },
  { id: 'zoom', icon: Video, name: 'Zoom', desc: 'Agenda y únete a reuniones.', steps: 'Requiere Zoom App (Client ID/Secret). Cada usuario autoriza su cuenta.' },
  { id: 'stripe', icon: CreditCard, name: 'Stripe', desc: 'Gestión de suscripciones y pagos.', steps: 'Requiere claves de Stripe. Se activa en la fase de pagos.' },
  { id: 'plaid', icon: Landmark, name: 'Plaid', desc: 'Saldos y transacciones bancarias.', steps: 'Requiere credenciales de Plaid (sandbox/production). Conexión bancaria cifrada por-usuario.' },
];

export default function SettingsPage() {
  const { user, updateProfile, changePassword } = useAuth();
  const { theme, setTheme } = useTheme();
  const [tab, setTab] = useState('profile');
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const savePassword = async () => {
    if (pwd.next.length < 6) return toast.error('La nueva contraseña debe tener al menos 6 caracteres');
    if (pwd.next !== pwd.confirm) return toast.error('Las contraseñas no coinciden');
    try { await changePassword(pwd.current, pwd.next); setPwd({ current: '', next: '', confirm: '' }); toast.success('Contraseña actualizada'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo cambiar la contraseña'); }
  };
  const [name, setName] = useState(user?.name || '');
  const [entry, setEntry] = useState(user?.entry_view || 'calendar_hero');
  const [pin, setPin] = useState('');
  const [conns, setConns] = useState([]);
  const avatarRef = useRef();
  const [card, setCard] = useState({ title: user?.title || '', company: user?.company || '', phone: user?.phone || '', website: user?.website || '' });

  useEffect(() => { setName(user?.name || ''); setEntry(user?.entry_view || 'calendar_hero'); setCard({ title: user?.title || '', company: user?.company || '', phone: user?.phone || '', website: user?.website || '' }); }, [user]);
  useEffect(() => { if (user?.role === 'owner') api.get('/integrations').then(({ data }) => setConns(data)).catch(() => {}); }, [user]);

  const onAvatar = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const img = new Image(); const reader = new FileReader();
    reader.onload = () => { img.onload = async () => {
      const c = document.createElement('canvas'); const s = 256; c.width = s; c.height = s;
      const ctx = c.getContext('2d'); const scale = Math.max(s / img.width, s / img.height);
      const w = img.width * scale, h = img.height * scale;
      ctx.drawImage(img, (s - w) / 2, (s - h) / 2, w, h);
      const b64 = c.toDataURL('image/jpeg', 0.85);
      await updateProfile({ avatar: b64 }); toast.success('Foto de perfil actualizada');
    }; img.src = reader.result; };
    reader.readAsDataURL(file);
  };

  const saveCard = async () => { await updateProfile(card); toast.success('Datos de tarjeta guardados'); };

  const downloadCard = () => {
    const c = document.createElement('canvas'); c.width = 1000; c.height = 600; const ctx = c.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 1000, 600); g.addColorStop(0, '#07090A'); g.addColorStop(1, '#1A4731');
    ctx.fillStyle = g; ctx.fillRect(0, 0, 1000, 600);
    ctx.fillStyle = 'rgba(62,180,137,0.15)'; ctx.beginPath(); ctx.arc(880, 120, 160, 0, Math.PI * 2); ctx.fill();

    // Logo de marca precargado para dibujar en la esquina inferior derecha
    const brand = new Image(); brand.crossOrigin = 'anonymous';

    const drawBrandLogo = () => {
      try {
        const bs = 96, bx = 1000 - bs - 40, by = 600 - bs - 78, rad = 16;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(bx + rad, by); ctx.arcTo(bx + bs, by, bx + bs, by + bs, rad);
        ctx.arcTo(bx + bs, by + bs, bx, by + bs, rad); ctx.arcTo(bx, by + bs, bx, by, rad);
        ctx.arcTo(bx, by, bx + bs, by, rad); ctx.closePath(); ctx.clip();
        ctx.fillStyle = '#fff'; ctx.fillRect(bx, by, bs, bs);
        if (brand.complete && brand.naturalWidth) ctx.drawImage(brand, bx, by, bs, bs);
        ctx.restore();
        ctx.strokeStyle = '#C9A84C'; ctx.lineWidth = 3; ctx.strokeRect(bx, by, bs, bs);
      } catch (err) { console.debug('No se pudo dibujar el logo en la tarjeta:', err); }
    };

    const finish = () => {
      const a = document.createElement('a'); a.href = c.toDataURL('image/png'); a.download = `tarjeta-${(user?.name || 'plannerpro').replace(/\s/g, '_')}.png`; a.click();
      toast.success('Tarjeta descargada');
    };

    const paintText = () => {
      ctx.fillStyle = '#A8E6CE'; ctx.font = 'bold 30px Inter, sans-serif'; ctx.fillText('PlannerPRO', 70, 90);
      ctx.fillStyle = '#fff'; ctx.font = 'bold 56px Inter, sans-serif'; ctx.fillText(user?.name || '', 70, 300);
      ctx.fillStyle = '#C9A84C'; ctx.font = '28px Inter, sans-serif'; ctx.fillText(card.title || '', 70, 348);
      ctx.fillStyle = '#dfeee7'; ctx.font = '24px Inter, sans-serif';
      let y = 400; const fields = [['Empresa', card.company], ['Tel', card.phone], ['Email', user?.email], ['Web', card.website]];
      fields.filter(([, v]) => v).forEach(([k, v]) => { ctx.fillText(`${k}:  ${v}`, 70, y); y += 40; });
      // Pie de marca
      ctx.strokeStyle = 'rgba(168,230,206,0.3)'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(70, 545); ctx.lineTo(820, 545); ctx.stroke();
      ctx.fillStyle = '#fff'; ctx.font = 'bold 22px Inter, sans-serif'; ctx.fillText('PlannerPRO', 70, 575);
      ctx.fillStyle = '#A8E6CE'; ctx.font = '18px Inter, sans-serif'; ctx.fillText('www.plannerpro.win', 70, 545 - 6 + 46);
      ctx.strokeStyle = '#3EB489'; ctx.lineWidth = 4; ctx.strokeRect(20, 20, 960, 560);
      drawBrandLogo();
      finish();
    };

    const paintAll = () => {
      // Foto de perfil grande (arriba-derecha, rounded)
      if (user?.avatar) {
        const av = new Image();
        av.crossOrigin = 'anonymous';
        av.onload = () => {
          const bx = 640, by = 70, bs = 260, rad = 24;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(bx + rad, by); ctx.arcTo(bx + bs, by, bx + bs, by + bs, rad);
          ctx.arcTo(bx + bs, by + bs, bx, by + bs, rad); ctx.arcTo(bx, by + bs, bx, by, rad);
          ctx.arcTo(bx, by, bx + bs, by, rad); ctx.closePath(); ctx.clip();
          const scale = Math.max(bs / av.width, bs / av.height);
          const w = av.width * scale, h = av.height * scale;
          ctx.drawImage(av, bx + (bs - w) / 2, by + (bs - h) / 2, w, h);
          ctx.restore();
          ctx.strokeStyle = '#C9A84C'; ctx.lineWidth = 5; ctx.strokeRect(bx, by, bs, bs);
          paintText();
        };
        av.onerror = paintText;
        av.src = user.avatar;
      } else {
        paintText();
      }
    };

    brand.onload = paintAll;
    brand.onerror = paintAll;
    brand.src = '/brand/logo-principal.jpg';
  };

  const connect = async (it) => {
    if (user?.role !== 'owner') return toast.error('Solo el propietario puede conectar cuentas');
    const label = window.prompt(`Etiqueta para tu cuenta de ${it.name} (ej. "Cuenta empresa")`);
    if (!label) return;
    try {
      const { data } = await api.post('/integrations', { provider: it.id, account_label: label });
      setConns([...conns, data]);
      toast.success(`${it.name} conectado: ${label}. Paso siguiente (OAuth real) se habilita al cargar credenciales del proveedor.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al conectar'); }
  };
  const disconnect = async (id) => { await api.delete(`/integrations/${id}`); setConns(conns.filter((c) => c.id !== id)); };

  const saveProfile = async () => { await updateProfile({ name, entry_view: entry, theme }); sessionStorage.removeItem('pp_entry_applied'); toast.success('Perfil actualizado. Tu vista de entrada se aplicará al volver al Panel.'); };
  const savePin = async () => { if (pin.length < 4) return toast.error('PIN mín 4 dígitos'); await updateProfile({ vault_pin: pin }); setPin(''); toast.success('PIN de bóveda configurado'); };

  const [suggestion, setSuggestion] = useState({ subject: '', body: '' });
  const sendSuggestion = async () => {
    if (!suggestion.subject.trim()) return toast.error('Escribe un asunto');
    try { await api.post('/suggestions', suggestion); toast.success('¡Gracias! Tu mensaje llegó al equipo PlannerPRO.'); setSuggestion({ subject: '', body: '' }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  // ---- Respaldos ----
  const [backup, setBackup] = useState(null);
  const [exporting, setExporting] = useState(false);
  useEffect(() => { if (tab === 'backup' && user?.role === 'owner') api.get('/backup/summary').then(({ data }) => setBackup(data)).catch(() => {}); }, [tab, user]);
  const exportBackup = async () => {
    setExporting(true);
    try {
      const { data } = await api.get('/backup/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `plannerpro-respaldo-${stamp}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Respaldo descargado correctamente');
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo exportar el respaldo'); }
    setExporting(false);
  };

  return (
    <div data-testid="settings-module">
      <PageHeader kicker="Configuración" title="Ajustes" />
      <Tabs value={tab} onValueChange={setTab} className="mb-4"><TabsList className="pp-glass"><TabsTrigger value="profile">Perfil</TabsTrigger><TabsTrigger value="card" data-testid="tab-card">Tarjeta</TabsTrigger><TabsTrigger value="security">Seguridad</TabsTrigger><TabsTrigger value="integrations">Integraciones</TabsTrigger><TabsTrigger value="backup" data-testid="tab-backup">Respaldos</TabsTrigger><TabsTrigger value="buzon" data-testid="tab-buzon">Buzón</TabsTrigger></TabsList></Tabs>

      {tab === 'profile' && (
        <div className="pp-glass p-6 max-w-xl space-y-5">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">{user?.avatar && <AvatarImage src={user.avatar} />}<AvatarFallback className="bg-[rgba(62,180,137,0.2)] pp-mint text-xl font-bold">{user?.name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
            <div>
              <input ref={avatarRef} type="file" accept="image/*" onChange={onAvatar} className="hidden" data-testid="avatar-input" />
              <button data-testid="avatar-upload-btn" onClick={() => avatarRef.current?.click()} className="h-9 px-4 rounded-xl text-sm border flex items-center gap-2"><Camera size={15} className="pp-mint" /> Cambiar foto</button>
              <div className="text-[11px] text-muted-foreground mt-1">JPG/PNG, se recorta a cuadrado.</div>
            </div>
          </div>
          <div><label className="text-sm text-muted-foreground">Nombre</label><input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-11 px-3 mt-1 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" /></div>
          <div>
            <label className="text-sm text-muted-foreground">Tema</label>
            <div className="flex gap-2 mt-1">
              <button data-testid="theme-dark" onClick={() => setTheme('dark')} className={`flex-1 h-11 rounded-xl flex items-center justify-center gap-2 border ${theme === 'dark' ? 'pp-mint pp-glow-mint border-[#3EB489]' : ''}`}><Moon size={16} /> Oscuro</button>
              <button data-testid="theme-light" onClick={() => setTheme('light')} className={`flex-1 h-11 rounded-xl flex items-center justify-center gap-2 border ${theme === 'light' ? 'pp-mint pp-glow-mint border-[#3EB489]' : ''}`}><Sun size={16} /> Claro</button>
            </div>
          </div>
          {user?.role === 'owner' && (
            <div><label className="text-sm text-muted-foreground">Vista de entrada</label>
              <p className="text-[11px] text-muted-foreground mb-1">Pantalla que verás al iniciar sesión o abrir la app.</p>
              <select data-testid="entry-view-select" value={entry} onChange={(e) => setEntry(e.target.value)} className="w-full h-11 px-3 mt-1 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]">
                <option value="daily_digest">Panel ejecutivo (recomendado)</option>
                <option value="calendar_hero">Calendario</option>
                <option value="financial_hub">Finanzas</option>
                <option value="project_board">Tareas y Proyectos</option>
              </select>
            </div>
          )}
          <button data-testid="save-profile" onClick={saveProfile} className="h-11 px-6 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Guardar</button>
        </div>
      )}

      {tab === 'card' && (
        <div className="grid md:grid-cols-2 gap-4 max-w-3xl" data-testid="business-card">
          <div className="pp-glass p-6 space-y-3">
            <div className="flex items-center gap-2 mb-1"><IdCard className="pp-mint" size={18} /><span className="font-semibold">Tarjeta de presentación</span></div>
            <p className="text-xs text-muted-foreground">Elige tus datos. Se genera con la marca PlannerPRO y puedes descargarla.</p>
            <input value={card.title} onChange={(e) => setCard({ ...card, title: e.target.value })} placeholder="Cargo / título (ej. CEO)" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <input value={card.company} onChange={(e) => setCard({ ...card, company: e.target.value })} placeholder="Empresa" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <input value={card.phone} onChange={(e) => setCard({ ...card, phone: e.target.value })} placeholder="Teléfono" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <input value={card.website} onChange={(e) => setCard({ ...card, website: e.target.value })} placeholder="Sitio web" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <div className="flex gap-2">
              <button onClick={saveCard} className="flex-1 h-11 rounded-xl border text-sm">Guardar datos</button>
              <button data-testid="download-card" onClick={downloadCard} className="flex-1 h-11 rounded-xl font-semibold text-[#07090A] flex items-center justify-center gap-2" style={{ background: '#3EB489' }}><Download size={16} /> Descargar</button>
            </div>
          </div>
          <div className="rounded-2xl p-6 flex flex-col justify-between pp-glow-mint relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#07090A,#1A4731)', minHeight: 300, border: '2px solid #3EB489' }}>
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm font-bold pp-mint">PlannerPRO</div>
              {user?.avatar
                ? <img src={user.avatar} alt={user?.name} className="w-40 h-40 rounded-2xl object-cover border-2 shrink-0" style={{ borderColor: '#C9A84C' }} data-testid="card-avatar" />
                : <div className="w-40 h-40 rounded-2xl grid place-items-center text-5xl font-black text-[#07090A] border-2 shrink-0" style={{ background: '#3EB489', borderColor: '#C9A84C' }}>{user?.name?.[0]?.toUpperCase()}</div>}
            </div>
            <div className="mt-2">
              <div className="text-2xl font-black text-white">{user?.name}</div>
              <div className="pp-gold text-sm">{card.title || 'Tu cargo'}</div>
              <div className="text-xs text-[#dfeee7] space-y-0.5 mt-2">
                {card.company && <div>{card.company}</div>}
                {card.phone && <div>{card.phone}</div>}
                <div>{user?.email}</div>
                {card.website && <div>{card.website}</div>}
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-[rgba(168,230,206,0.2)] flex items-center justify-between">
              <span className="text-xs font-bold text-white">Planner<span className="pp-gold">PRO</span></span>
              <span className="text-[11px] text-[#A8E6CE]">www.plannerpro.win</span>
            </div>
            {/* Logo PlannerPRO en la esquina inferior derecha (elevado para no tapar la web) */}
            <img src="/brand/logo-principal.jpg" alt="PlannerPRO" data-testid="card-brand-logo"
              className="absolute bottom-12 right-3 w-14 h-14 rounded-xl object-cover shadow-lg"
              style={{ border: '2px solid #C9A84C', boxShadow: '0 0 18px rgba(62,180,137,0.35)' }} />
          </div>
        </div>
      )}

      {tab === 'security' && (
        <div className="space-y-4 max-w-xl">
          <div className="pp-glass p-6" data-testid="change-password-card">
            <div className="flex items-center gap-2 mb-2"><Lock className="pp-mint" size={18} /><span className="font-semibold">Cambiar mi contraseña</span></div>
            <p className="text-sm text-muted-foreground mb-4">Introduce tu contraseña actual y la nueva. Se aplica de inmediato.</p>
            <div className="space-y-2">
              <input data-testid="current-password" type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} placeholder="Contraseña actual" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
              <input data-testid="new-password" type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} placeholder="Nueva contraseña (mín. 6)" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
              <input data-testid="confirm-password" type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} placeholder="Repite la nueva contraseña" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
              <button data-testid="save-password-btn" onClick={savePassword} className="h-11 px-6 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Actualizar contraseña</button>
            </div>
          </div>
          {user?.role === 'owner' && (
            <div className="pp-glass p-6">
              <div className="flex items-center gap-2 mb-2"><Lock className="pp-gold" size={18} /><span className="font-semibold">PIN de la Bóveda Zero-Knowledge</span></div>
              <p className="text-sm text-muted-foreground mb-4">Este PIN protege tus gastos privados. La clave se deriva localmente; el servidor nunca la almacena en texto plano.</p>
              <div className="flex gap-2"><input data-testid="vault-pin-set" type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Nuevo PIN" className="flex-1 h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#C9A84C]" /><button data-testid="save-pin" onClick={savePin} className="h-11 px-5 rounded-xl font-semibold text-[#07090A]" style={{ background: '#C9A84C' }}>Guardar PIN</button></div>
            </div>
          )}
          <div className="pp-glass p-6">
            <div className="font-semibold mb-3">Seguridad de la cuenta</div>
            {['AES-256 cifrado en reposo', 'TLS 1.3 en tránsito', 'Aislamiento por cuenta (RLS)', 'Logs de auditoría inmutables', 'GDPR + CCPA'].map((s) => (
              <div key={s} className="flex items-center gap-2 text-sm text-muted-foreground py-1"><Check size={15} className="pp-mint" /> {s}</div>
            ))}
            <div className="mt-3 p-3 rounded-xl bg-[rgba(62,180,137,0.06)] text-xs text-muted-foreground flex gap-2"><Info size={14} className="pp-mint shrink-0 mt-0.5" /> MFA y WebAuthn (Face ID / Touch ID) se activan en la fase de hardening de seguridad.</div>
          </div>
        </div>
      )}

      {tab === 'integrations' && (
        <div className="max-w-3xl">
          {user?.role !== 'owner' && <div className="pp-glass p-4 mb-4 text-sm text-muted-foreground flex gap-2"><Info size={16} className="pp-mint shrink-0" /> Solo el propietario de la cuenta puede crear conexiones de integraciones.</div>}
          {conns.length > 0 && (
            <div className="pp-glass p-4 mb-4">
              <div className="text-sm font-semibold mb-2">Cuentas conectadas ({conns.length})</div>
              <div className="space-y-2">
                {conns.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-[rgba(62,180,137,0.08)]">
                    <Check size={14} className="pp-mint" /><span className="flex-1">{c.provider} · {c.account_label}</span>
                    <button onClick={() => disconnect(c.id)} className="text-xs text-red-400">Desconectar</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            {INTEGRATIONS.map((it) => (
              <div key={it.id} className="pp-glass p-5" data-testid={`integration-${it.id}`}>
                <div className="flex items-center gap-3 mb-2"><span className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: 'rgba(62,180,137,0.12)' }}><it.icon size={18} className="pp-mint" /></span><div><div className="font-semibold text-sm">{it.name}</div><div className="text-xs text-muted-foreground">{it.desc}</div></div></div>
                <p className="text-[11px] text-muted-foreground mb-3">{it.steps}</p>
                <button data-testid={`connect-${it.id}`} onClick={() => connect(it)} disabled={user?.role !== 'owner'} className="w-full h-9 rounded-lg text-xs border flex items-center justify-center gap-1 disabled:opacity-40"><Plug size={13} /> Conectar</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'backup' && (
        <div className="space-y-4 max-w-3xl" data-testid="backup-tab">
          {user?.role !== 'owner' ? (
            <div className="pp-glass p-6 text-sm text-muted-foreground flex gap-2" data-testid="backup-locked"><Info size={16} className="pp-mint shrink-0" /> Solo el propietario de la cuenta puede exportar y configurar respaldos.</div>
          ) : (
            <>
              <div className="pp-glass p-6">
                <div className="flex items-center gap-2 mb-2"><Database className="pp-mint" size={18} /><span className="font-semibold">Respaldo manual</span></div>
                <p className="text-sm text-muted-foreground mb-4">Descarga una copia completa de tus datos en formato JSON. Puedes guardarla donde quieras y reimportarla más adelante.</p>
                {backup && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="backup-summary">
                    <div className="p-3 rounded-xl bg-[rgba(62,180,137,0.08)] text-center"><div className="text-2xl font-black pp-mint">{backup.total}</div><div className="text-[11px] text-muted-foreground">registros</div></div>
                    <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.04)] text-center"><div className="text-lg font-bold">{backup.counts?.tasks || 0}</div><div className="text-[11px] text-muted-foreground">tareas</div></div>
                    <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.04)] text-center"><div className="text-lg font-bold">{backup.counts?.transactions || 0}</div><div className="text-[11px] text-muted-foreground">finanzas</div></div>
                    <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.04)] text-center"><div className="text-lg font-bold">{backup.counts?.email_messages || 0}</div><div className="text-[11px] text-muted-foreground">correos</div></div>
                  </div>
                )}
                <button data-testid="export-backup-btn" onClick={exportBackup} disabled={exporting} className="h-11 px-6 rounded-xl font-semibold text-[#07090A] flex items-center gap-2 disabled:opacity-60" style={{ background: '#3EB489' }}>
                  {exporting ? <><Loader2 size={16} className="animate-spin" /> Generando...</> : <><HardDriveDownload size={16} /> Descargar respaldo (JSON)</>}
                </button>
              </div>

              <div className="pp-glass p-6">
                <div className="flex items-center gap-2 mb-2"><Cloud className="pp-gold" size={18} /><span className="font-semibold">Respaldo automático en la nube</span></div>
                <p className="text-sm text-muted-foreground mb-4">Programa copias automáticas a tu almacenamiento en la nube. Se habilita tras el despliegue con la conexión OAuth del proveedor.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[{ n: 'Google Drive', d: 'Copia diaria/semanal a tu Drive' }, { n: 'Dropbox', d: 'Sincroniza tus respaldos con Dropbox' }].map((p) => (
                    <div key={p.n} className="p-4 rounded-xl border flex items-center gap-3" data-testid={`cloud-${p.n.split(' ')[0].toLowerCase()}`}>
                      <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(201,168,76,0.12)' }}><Cloud size={18} className="pp-gold" /></span>
                      <div className="flex-1 min-w-0"><div className="font-semibold text-sm">{p.n}</div><div className="text-[11px] text-muted-foreground">{p.d}</div></div>
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full border border-[rgba(201,168,76,0.5)] pp-gold shrink-0">Post-despliegue</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'buzon' && (
        <div className="grid md:grid-cols-2 gap-4 max-w-3xl" data-testid="buzon-tab">
          <div className="pp-glass p-6 space-y-3">
            <div className="flex items-center gap-2"><IdCard className="pp-mint" size={18} /><span className="font-semibold">Buzón de sugerencias</span></div>
            <p className="text-xs text-muted-foreground">Envía ideas, reportes o peticiones directo al equipo PlannerPRO (Super Master).</p>
            <input data-testid="suggestion-subject" value={suggestion.subject} onChange={(e) => setSuggestion({ ...suggestion, subject: e.target.value })} placeholder="Asunto" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <textarea data-testid="suggestion-body" value={suggestion.body} onChange={(e) => setSuggestion({ ...suggestion, body: e.target.value })} placeholder="Cuéntanos con detalle..." className="w-full h-28 p-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <button data-testid="send-suggestion" onClick={sendSuggestion} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Enviar al equipo</button>
          </div>
          <div className="pp-glass p-6">
            <div className="flex items-center gap-2 mb-3"><Plug className="pp-gold" size={18} /><span className="font-semibold">Herramientas conectadas</span></div>
            {user?.role === 'owner' ? (
              conns.length > 0 ? (
                <div className="space-y-2" data-testid="connected-tools">
                  {conns.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-[rgba(62,180,137,0.08)]"><Check size={14} className="pp-mint" /><span className="flex-1 capitalize">{c.provider} · {c.account_label}</span></div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No tienes herramientas conectadas. Ve a la pestaña Integraciones para conectar Google, Outlook, Zoom, etc.</p>
            ) : <p className="text-sm text-muted-foreground">Solo el propietario gestiona integraciones.</p>}
            <button onClick={() => setTab('integrations')} className="mt-4 h-9 px-4 rounded-xl border text-sm pp-mint">Gestionar integraciones</button>
          </div>
        </div>
      )}
    </div>
  );
}
